import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAdmin } from '../../context/AdminContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Activity, Mail, Lock, Loader2, AlertCircle, Shield } from 'lucide-react';
import { toast } from 'sonner';

const AdminLoginPage = () => {
    const { t } = useTranslation();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const { login } = useAdmin();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            await login(email, password, rememberMe);
            toast.success(t('notifications.loginSuccess'));
            navigate('/admin/dashboard');
        } catch (err) {
            const message = err.response?.data?.detail || t('errors.loginFailed');
            setError(message);
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="admin-login-page">
            <div className="w-full max-w-md p-6">
                {/* Logo */}
                <div className="flex justify-center mb-8">
                    <div className="flex items-center gap-3">
                        <img 
                            src="/logo.svg" 
                            alt={t('app.name')} 
                            className="h-14 w-auto"
                        />
                        <div>
                            <span className="text-2xl font-bold text-white">{t('app.name')}</span>
                            <span className="block text-sm text-slate-400">{t('admin.adminPortal')}</span>
                        </div>
                    </div>
                </div>

                <Card className="border-0 shadow-2xl bg-slate-800/50 backdrop-blur-sm border border-slate-700">
                    <CardContent className="p-8">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 rounded-2xl bg-slate-700 flex items-center justify-center mx-auto mb-4">
                                <Shield className="w-8 h-8 text-teal-400" />
                            </div>
                            <h2 className="text-2xl font-bold text-white mb-2">{t('admin.loginTitle')}</h2>
                            <p className="text-slate-400">{t('admin.loginSubtitle')}</p>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-5">
                            {error && (
                                <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm" data-testid="admin-login-error">
                                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                    {error}
                                </div>
                            )}

                            <div className="space-y-2">
                                <Label htmlFor="email" className="text-slate-300">{t('auth.email')}</Label>
                                <div className="relative">
                                    <Mail className="absolute start-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                                    <Input
                                        id="email"
                                        type="email"
                                        placeholder="admin@natijati.com"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        className="ps-10 h-12 rounded-xl bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-teal-500 focus:ring-teal-500/20"
                                        data-testid="admin-email-input"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="password" className="text-slate-300">{t('auth.password')}</Label>
                                <div className="relative">
                                    <Lock className="absolute start-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                                    <Input
                                        id="password"
                                        type="password"
                                        placeholder="••••••••"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        className="ps-10 h-12 rounded-xl bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-teal-500 focus:ring-teal-500/20"
                                        data-testid="admin-password-input"
                                    />
                                </div>
                            </div>

                            {/* Remember Me */}
                            <label className="flex items-center gap-2 cursor-pointer group">
                                <input
                                    type="checkbox"
                                    checked={rememberMe}
                                    onChange={(e) => setRememberMe(e.target.checked)}
                                    className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-teal-500 focus:ring-teal-500/20 cursor-pointer"
                                    data-testid="admin-remember-me-checkbox"
                                />
                                <span className="text-sm text-slate-400 group-hover:text-slate-300">{t('admin.keepMeLoggedIn')}</span>
                            </label>

                            <Button
                                type="submit"
                                disabled={loading}
                                className="w-full h-12 bg-teal-500 hover:bg-teal-600 text-white rounded-xl font-medium shadow-lg shadow-teal-500/25 transition-all"
                                data-testid="admin-login-btn"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="me-2 w-5 h-5 animate-spin" />
                                        {t('admin.signingIn')}
                                    </>
                                ) : (
                                    t('admin.signInToDashboard')
                                )}
                            </Button>
                        </form>

                        <div className="mt-6 pt-6 border-t border-slate-700">
                            <p className="text-xs text-slate-500 text-center">
                                🔒 {t('admin.secureArea')}
                            </p>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default AdminLoginPage;
